import SaveAndCloseOrAddButton from "@app/components/save-and-close-or-add-button.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useBudgetData from "@app/data-hooks/use-budget-data";
import useCategoryData from "@app/data-hooks/use-category-data.ts";
import useLookupListData from "@app/data-hooks/use-lookup-list-data.ts";
import useRosterData from "@app/data-hooks/use-roster-data.ts";
import useWorkPackageData from "@app/data-hooks/use-work-package-data.ts";
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid2,
    TextField,
} from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { useStore } from "@tanstack/react-store";
import { CurrencyInput, Dialog, QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useRef } from "react";

interface Props {
    open: boolean;
    id?: number;
    assignmentId: number;
    onClose: (addNew?: boolean) => void;
    catId: number;
    initialStaffId?: number;
}

export default function AssignmentBudgetModal({ open, id, assignmentId, onClose, catId, initialStaffId }: Props) {
    const { categories, subCategoryGroupsByCategoryId } = useCategoryData({
        entryCategoryId: open ? catId : undefined,
    });
    const { entryDetails, saveEntry } = useBudgetData({ id: open ? id : undefined });
    const { rosterList, assignmentPhaseList } = useAssignmentData({
        id: assignmentId,
        activeState: ActiveEnums.ActiveOnly,
        assignmentIds: open ? assignmentId.toString() : undefined,
    });
    const { projectRoles } = useLookupListData();

    const form = useForm({
        defaultValues: {
            categoryId: entryDetails.data?.categoryId ?? catId,
            subCategoryGroupId:
                entryDetails.data?.subcategoryGroupId ??
                ((subCategoryGroupsByCategoryId.data?.length ?? 0) == 1
                    ? subCategoryGroupsByCategoryId.data![0].subcategoryGroupId
                    : undefined),
            staffId: entryDetails.data?.staffId ?? initialStaffId,
            phaseId: entryDetails.data?.assignmentPhaseId,
            workPackageId: entryDetails.data?.assignmentWorkPackageId,
            roleId: entryDetails.data?.projectRoleId,
            description: entryDetails.data?.description,
            quantity: entryDetails.data?.quantity,
            invoiceVisible: entryDetails.data?.invoiceVisible ?? true,
            billable: entryDetails.data?.billable ?? true,
        },
        onSubmit: async ({ value }) => {
            await saveEntry.mutateAsync({
                id,
                assignmentId,
                categoryId: value.categoryId,
                subCategoryGroupId: value.subCategoryGroupId,
                staffId: value.staffId,
                phaseId: value.phaseId,
                workPackageId: value.workPackageId,
                roleId: value.roleId,
                description: value.description,
                quantity: value.quantity,
                invoiceVisible: value.invoiceVisible,
                billable: value.billable,
            });
        },
    });

    const staffId = useStore(form.store, (state) => state.values.staffId);
    const { projectRoleId } = useRosterData({ assignmentId, staffId });

    useEffect(() => {
        if (projectRoleId.data == undefined || !open) {
            return;
        }
        form.setFieldValue("roleId", projectRoleId.data);
    }, [projectRoleId.data, open]);

    const phaseId = useStore(form.store, (state) => state.values.phaseId);
    const { filterWorkPackagesList } = useWorkPackageData({
        assignmentId: open ? assignmentId : undefined,
        activeOnly: true,
        phaseId: phaseId,
    });

    const formRef = useRef<HTMLFormElement>(null);

    const handleClose = (addNew?: boolean) => {
        onClose(addNew);
        if (!addNew) {
            form.reset();
        }
    };

    return (
        <Dialog open={open} onClose={() => handleClose()} maxWidth={"md"} fullWidth>
            <DialogTitle>{id == undefined ? "Create" : "Edit"} Budget</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
                ref={formRef}
            >
                <DialogContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={6}>
                            <QueryStatusRouter queryHook={categories}>
                                <form.Field name={"categoryId"}>
                                    {(field) => (
                                        <Select
                                            label={"Category"}
                                            value={field.state.value}
                                            required
                                            fullWidth
                                            disabled={true}
                                            onChange={() => {}}
                                            options={categories.data?.map((x) => ({
                                                label: x.categoryDescription,
                                                value: x.categoryId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        </Grid2>
                        <Grid2 size={6}>
                            <QueryStatusRouter queryHook={subCategoryGroupsByCategoryId}>
                                <form.Field name={"subCategoryGroupId"}>
                                    {(field) => (
                                        <Select
                                            label={"Expense Group"}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            required
                                            fullWidth
                                            options={subCategoryGroupsByCategoryId.data?.map((x) => ({
                                                label: x.name,
                                                value: x.subcategoryGroupId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        </Grid2>
                        <Grid2 size={6}>
                            <QueryStatusRouter queryHook={rosterList}>
                                <form.Field name={"staffId"}>
                                    {(field) => (
                                        <Select
                                            label={"Staff"}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            required
                                            fullWidth
                                            options={rosterList.data?.map((x) => ({
                                                label: `${x.lastName}, ${x.firstName}`,
                                                value: x.staffId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        </Grid2>
                        {catId == 1 && (
                            <Grid2 size={6}>
                                <QueryStatusRouter queryHook={assignmentPhaseList}>
                                    <form.Field name={"phaseId"}>
                                        {(field) => (
                                            <Select
                                                label={"Phase"}
                                                value={field.state.value}
                                                onChange={(e) => field.handleChange(e)}
                                                required
                                                fullWidth
                                                options={assignmentPhaseList.data?.map((x) => ({
                                                    label: x.displayName,
                                                    value: x.assignmentPhaseId,
                                                }))}
                                            />
                                        )}
                                    </form.Field>
                                </QueryStatusRouter>
                            </Grid2>
                        )}
                        {catId == 1 && (
                            <Grid2 size={6}>
                                <QueryStatusRouter queryHook={filterWorkPackagesList}>
                                    <form.Subscribe selector={(state) => state.values.phaseId}>
                                        {(phaseId) => (
                                            <form.Field name={"workPackageId"}>
                                                {(field) => (
                                                    <Select
                                                        label={"Work Package"}
                                                        value={field.state.value}
                                                        onChange={(e) => field.handleChange(e)}
                                                        required
                                                        fullWidth
                                                        disabled={phaseId == undefined}
                                                        options={filterWorkPackagesList.data?.map((x) => ({
                                                            label: x.displayName,
                                                            value: x.workPackageId,
                                                        }))}
                                                    />
                                                )}
                                            </form.Field>
                                        )}
                                    </form.Subscribe>
                                </QueryStatusRouter>
                            </Grid2>
                        )}
                        <Grid2 size={6}>
                            <QueryStatusRouter queryHook={projectRoles}>
                                <form.Field name={"roleId"}>
                                    {(field) => (
                                        <Select
                                            label={"Role"}
                                            value={field.state.value}
                                            onChange={(e) => field.handleChange(e)}
                                            required
                                            fullWidth
                                            options={projectRoles.data?.map((x) => ({
                                                label: x.roleDescription,
                                                value: x.projectRoleId,
                                            }))}
                                        />
                                    )}
                                </form.Field>
                            </QueryStatusRouter>
                        </Grid2>
                        <Grid2 size={12}>
                            <form.Field name={"description"}>
                                {(field) => (
                                    <TextField
                                        multiline
                                        label={"Description"}
                                        minRows={3}
                                        value={field.state.value ?? ""}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        fullWidth
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                        <Grid2 size={6}>
                            <form.Field
                                name={"quantity"}
                                validators={{
                                    onChange: ({ value }) => {
                                        if (value == undefined) {
                                            return;
                                        }
                                        if (value <= 0) {
                                            return "Must be greater than 0";
                                        }
                                    },
                                }}
                            >
                                {(field) =>
                                    catId == 1 ? (
                                        <TextField
                                            type={"number"}
                                            label={"Hours"}
                                            value={field.state.value ?? ""}
                                            onChange={(e) => field.handleChange(+e.target.value)}
                                            required
                                            helperText={field.state.meta.errors.join(", ")}
                                            error={field.state.meta.errors.length > 0}
                                            fullWidth
                                        />
                                    ) : (
                                        <CurrencyInput
                                            type={"number"}
                                            label={"Amount"}
                                            value={field.state.value ?? ""}
                                            onChange={(e) => field.handleChange(e)}
                                            required
                                            helperText={field.state.meta.errors.join(", ")}
                                            error={field.state.meta.errors.length > 0}
                                            fullWidth
                                        />
                                    )
                                }
                            </form.Field>
                        </Grid2>
                        <Grid2 size={12}>
                            <form.Field name={"invoiceVisible"}>
                                {(field) => (
                                    <FormControlLabel
                                        label={"Invoice Visible"}
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                    />
                                )}
                            </form.Field>
                            <form.Field name={"billable"}>
                                {(field) => (
                                    <FormControlLabel
                                        label={"Billable"}
                                        control={
                                            <Checkbox
                                                checked={field.state.value ?? false}
                                                onChange={(e) => field.handleChange(e.target.checked)}
                                            />
                                        }
                                    />
                                )}
                            </form.Field>
                        </Grid2>
                    </Grid2>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={() => handleClose()}>
                        Close
                    </Button>
                    <SaveAndCloseOrAddButton
                        onSave={form.handleSubmit}
                        onSecondary={() => {
                            handleClose(true);
                        }}
                        secondaryActionTitle={id == undefined ? "Add New" : "Close"}
                        formRef={formRef}
                    />
                </DialogActions>
            </form>
        </Dialog>
    );
}
